import React, { useState, useEffect } from "react";
import useAdminSettings from "../../hooks/useAdminSettings";
import useFileUploader from "../../hooks/useFileUploader";
import employeeService from "../../api/services/employeeService";
import usePath from "../../hooks/usePath";
import Swal from "sweetalert2";
import SafeImage from "../../components/common/SafeImage";

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState("general");
  const [localSettings, setLocalSettings] = useState({});
  const [saveStatus, setSaveStatus] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);

  const fileuploader = useFileUploader();

  const {
    settings,
    loading,
    error,
    GetEmployees,
    updateSettings,
    uploadFile,
    resetToDefaults: resetSettings,
    resetToDefaultsStatus: resetLoading,
    resetToDefaultsError: resetError,
    getSettingValue,
    setSettingValue,
  } = useAdminSettings();

  // New local states for Order Place Message section
  const [allEmployees, setAllEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  //convert to array as string format
  const parseArraySetting = (value) => {
    if (Array.isArray(value)) {
      return value.map((v) => parseInt(String(v), 10)).filter((n) => !isNaN(n));
    }
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed
            .map((v) => parseInt(String(v), 10))
            .filter((n) => !isNaN(n));
        }
      } catch (e) {
        const parts = value
          .split(",")
          .map((v) => parseInt(String(v).trim(), 10))
          .filter((n) => !isNaN(n));
        if (parts.length) return parts;
      }
      return [];
    }
    return [];
  };

  //convert to boolean type value
  const toBoolean = (value, defaultValue = false) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value === "true" || value === "1";
    if (typeof value === "number") return value === 1;
    return defaultValue;
  };

  // Initialize local settings when backend settings load
  useEffect(() => {
    if (Object.keys(settings).length > 0) {
      // ---------------------------------
      // 1. Read shipping JSON
      // ---------------------------------
      let shippingJSON = {};
      try {
        const raw = getSettingValue("shipping.settings", "{}");
        shippingJSON = typeof raw === "string" ? JSON.parse(raw) : raw || {};
      } catch (e) {
        console.error("Invalid shipping.settings JSON", e);
        shippingJSON = {};
      }

      // ---------------------------------
      // 2. Read service charge JSON
      // ---------------------------------
      let serviceChargeJSON = {};
      try {
        const raw = getSettingValue("service_charge.settings", "{}");
        serviceChargeJSON =
          typeof raw === "string" ? JSON.parse(raw) : raw || {};
      } catch (e) {
        console.error("Invalid service_charge.settings JSON", e);
        serviceChargeJSON = {};
      }

      // ---------------------------------
      // 3. Map all settings
      // ---------------------------------
      const mappedSettings = {
        // Website
        websiteName: getSettingValue("website.name", ""),
        websiteLogo: getSettingValue("website.logo", null),
        favicon: getSettingValue("website.favicon", null),

        // Contact
        contactNumber: getSettingValue("contact.number", ""),
        whatsappNumber: getSettingValue("contact.whatsapp", ""),
        address: getSettingValue("contact.address", ""),
        email: getSettingValue("contact.email", ""),
        supportEmail: getSettingValue("contact.support_email", ""),
        facebook: getSettingValue("social.facebook", ""),
        twitter: getSettingValue("social.twitter", ""),
        instagram: getSettingValue("social.instagram", ""),
        linkedin: getSettingValue("social.linkedin", ""),
        youtube: getSettingValue("social.youtube", ""),

        // Driver assignment
        driverAssignment: getSettingValue("driver_assignment_auto_mode", true)
          ? "auto"
          : "manual",
        autoAssignmentRadius: getSettingValue(
          "driver_assignment_distance_range",
          10,
        ),
        driverTimeout: getSettingValue("driver_assignment_request_timeout", 15),
        // maximum drivers request for order
        driverAssignmentMaxRequests: getSettingValue(
          "driver_assignment_max_requests",
          5
        ),

        // Order message
        orderPlaceSendAdmin: toBoolean(
          getSettingValue("delivery.order_place_message.send_admin", false),
          false,
        ),
        orderPlaceEmployeeIds: parseArraySetting(
          getSettingValue("delivery.order_place_message.employee_ids", []),
        ),
        minimumCommission: getSettingValue("delivery.minimum_commission", 0),
        vendorCommission: getSettingValue("delivery.vendor_commission", 0),
        // Policies
        termsConditions: getSettingValue("policies.terms_conditions", ""),
        privacyPolicy: getSettingValue("policies.privacy_policy", ""),
        refundReturnPolicy: getSettingValue(
          "policies.refund_return_policy",
          "",
        ),

        // Maintenance
        maintenanceMode: toBoolean(
          getSettingValue("maintenance.mode", false),
          false,
        ),

        maintenanceMessage: getSettingValue("maintenance.message", ""),
        maintenanceImage: getSettingValue("maintenance.image", null),

        // Payments
        stripePublicKey: getSettingValue("payment.stripe_public_key", ""),
        stripeSecretKey: getSettingValue("payment.stripe_secret_key", ""),
        paypalClientId: getSettingValue("payment.paypal_client_id", ""),
        paypalClientSecret: getSettingValue("payment.paypal_client_secret", ""),

        // Twilio
        twilioAccountSid: getSettingValue("twilio.account_sid", ""),
        twilioAuthToken: getSettingValue("twilio.auth_token", ""),
        twilioPhoneNumber: getSettingValue("twilio.phone_number", ""),

        // Google
        // googleMapsApiKey: getSettingValue("google.maps_api_key", ""),
        googleClientId: getSettingValue("google.google_client_id", ""),
        googleClientSecret: getSettingValue("google.google_client_secret", ""),
        googleRedirectUrl: getSettingValue("google.google_redirect_url", ""),

        // ---------------------------------
        // Shipping Settings (JSON)
        // ---------------------------------
        shippingEnabled: shippingJSON.enabled ?? false,
        shippingType: shippingJSON.type || "",
        fixedAmount: shippingJSON.fixed_amount ?? 0,
        percentage: shippingJSON.percentage ?? 0,
        baseKm: shippingJSON.base_km ?? 0,
        basePrice: shippingJSON.base_price ?? 0,
        pricePerKm: shippingJSON.price_per_km ?? 0,

        // ---------------------------------
        // Service Charge Settings (JSON)
        // ---------------------------------
        serviceChargeEnabled: serviceChargeJSON.enabled ?? false,
        serviceChargePercentage: serviceChargeJSON.percentage ?? 0,

        // Admin Email
        adminEmail: getSettingValue("admin.admin_address", ""),
      };

      // ---------------------------------
      // 4. Set local settings
      // ---------------------------------
      setLocalSettings(mappedSettings);
    }
  }, [settings, getSettingValue]);

  // Fetch employees for dropdown
  useEffect(() => {
    let isMounted = true;
    setEmployeesLoading(GetEmployees.isLoading);
    if (!isMounted) return;
    if (!GetEmployees.isLoading && GetEmployees?.data?.data) {
      setAllEmployees(GetEmployees.data.data);
    }
    return () => {
      isMounted = false;
    };
  }, [GetEmployees.isLoading, GetEmployees.data]);

  const handleInputChange = (field, value) => {
    setLocalSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = async (field, e) => {
    const file = e.target.files[0];
    if (file) {
      let img_url = usePath(
        await fileuploader.uploadImage(file, "admin-settings", file.name),
      );
      if (img_url) {
        setLocalSettings((prev) => ({
          ...prev,
          [field]: img_url,
        }));
        // Persist immediately when maintenance image is uploaded
        if (field === "maintenanceImage") {
          updateSettings({ "maintenance.image": img_url });
        }
      }

      // // Map field to backend setting key
      // const settingKeyMap = {
      //     websiteLogo: 'website.logo',
      //     favicon: 'website.favicon',
      //     maintenanceImage: 'maintenance.image'
      // };

      // const settingKey = settingKeyMap[field];
      // if (settingKey) {
      //     // const result = await uploadFile(file, settingKey);

      //     let img_url = await fileuploader.uploadImage(file,"admin-settings",f)
      //     if (img_url) {
      //         setLocalSettings(prev => ({
      //             ...prev,
      //             [field]: img_url
      //         }));
      //     }

      // } else {
      //     setLocalSettings(prev => ({
      //         ...prev,
      //         [field]: file
      //     }));
      // }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // ============================
    // SHIPPING VALIDATION
    // ============================
    if (localSettings.shippingEnabled) {
      if (!localSettings.shippingType) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Please select a shipping type",
        });
        return;
      }

      if (
        localSettings.shippingType === "fixed" &&
        !localSettings.fixedAmount
      ) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Please enter Fixed Amount",
        });
        return;
      }

      if (
        localSettings.shippingType === "percentage" &&
        !localSettings.percentage
      ) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Please enter Percentage",
        });
        return;
      }

      if (localSettings.shippingType === "km_base") {
        if (!localSettings.baseKm) {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Please enter Base KM",
          });
          return;
        }
        if (!localSettings.basePrice) {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Please enter Base Price",
          });
          return;
        }
        if (!localSettings.pricePerKm) {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Please enter Price Per KM",
          });
          return;
        }
      }
    }

    // ============================
    // SERVICE CHARGE VALIDATION
    // ============================
    if (localSettings.serviceChargeEnabled) {
      if (
        !localSettings.serviceChargePercentage ||
        parseFloat(localSettings.serviceChargePercentage) <= 0
      ) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Please enter Service Charge Percentage",
        });
        return;
      }
    }

    setSaveStatus("saving");

    // ============================
    // SHIPPING SETTINGS JSON
    // ============================
    const shippingSettings = {
      enabled: !!localSettings.shippingEnabled,
      type: localSettings.shippingType || "",
      fixed_amount:
        localSettings.shippingType === "fixed"
          ? parseFloat(localSettings.fixedAmount)
          : 0,
      percentage:
        localSettings.shippingType === "percentage"
          ? parseFloat(localSettings.percentage)
          : 0,
      base_km:
        localSettings.shippingType === "km_base"
          ? parseFloat(localSettings.baseKm)
          : 0,
      base_price:
        localSettings.shippingType === "km_base"
          ? parseFloat(localSettings.basePrice)
          : 0,
      price_per_km:
        localSettings.shippingType === "km_base"
          ? parseFloat(localSettings.pricePerKm)
          : 0,
    };

    // ============================
    // SERVICE CHARGE JSON
    // ============================
    const serviceChargeSettings = {
      enabled: !!localSettings.serviceChargeEnabled,
      percentage: localSettings.serviceChargeEnabled
        ? parseFloat(localSettings.serviceChargePercentage)
        : 0,
    };

    // ============================
    // BACKEND PAYLOAD
    // ============================
    const backendSettings = {
      "website.name": localSettings.websiteName,
      "website.logo": localSettings.websiteLogo,
      "website.favicon": localSettings.favicon,
      "contact.number": localSettings.contactNumber,
      "contact.whatsapp": localSettings.whatsappNumber,
      "contact.address": localSettings.address,
      "contact.email": localSettings.email,
      "contact.support_email": localSettings.supportEmail,
      "social.facebook": localSettings.facebook,
      "social.twitter": localSettings.twitter,
      "social.instagram": localSettings.instagram,
      "social.linkedin": localSettings.linkedin,
      "social.youtube": localSettings.youtube,
      driver_assignment_auto_mode: localSettings.driverAssignment === "auto",
      driver_assignment_distance_range:
        localSettings.autoAssignmentRadius !== ""
          ? parseFloat(localSettings.autoAssignmentRadius)
          : 10,
      driver_assignment_request_timeout:
        localSettings.driverTimeout !== ""
          ? parseInt(localSettings.driverTimeout)
          : 15,
      driver_assignment_max_requests:
        localSettings.driverAssignmentMaxRequests !== ""
          ? parseInt(localSettings.driverAssignmentMaxRequests)
          : 5,
      "delivery.order_place_message.send_admin":
        !!localSettings.orderPlaceSendAdmin,
      "delivery.order_place_message.employee_ids": JSON.stringify(
        localSettings.orderPlaceEmployeeIds || [],
      ),
      "delivery.minimum_commission":parseFloat(localSettings.minimumCommission) || 0,
      "delivery.vendor_commission":parseFloat(localSettings.vendorCommission) || 0,
      "policies.terms_conditions": localSettings.termsConditions,
      "policies.privacy_policy": localSettings.privacyPolicy,
      "policies.refund_return_policy": localSettings.refundReturnPolicy,
      "maintenance.mode": localSettings.maintenanceMode,
      "maintenance.message": localSettings.maintenanceMessage,
      "maintenance.image": localSettings.maintenanceImage,
      "payment.stripe_public_key": localSettings.stripePublicKey,
      "payment.stripe_secret_key": localSettings.stripeSecretKey,
      "payment.paypal_client_id": localSettings.paypalClientId,
      "payment.paypal_client_secret": localSettings.paypalClientSecret,
      "twilio.account_sid": localSettings.twilioAccountSid,
      "twilio.auth_token": localSettings.twilioAuthToken,
      "twilio.phone_number": localSettings.twilioPhoneNumber,
      // "google.maps_api_key": localSettings.googleMapsApiKey,
      "google.google_client_id": localSettings.googleClientId,
      "google.google_client_secret": localSettings.googleClientSecret,
      "google.google_redirect_url": localSettings.googleRedirectUrl,

      "shipping.settings": JSON.stringify(shippingSettings),
      "service_charge.settings": JSON.stringify(serviceChargeSettings),
      "admin.admin_address": localSettings.adminEmail,
    };

    // ============================
    // API REQUEST
    // ============================
    updateSettings(backendSettings, {
      onSuccess: () => {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Settings saved successfully",
          timer: 2000,
          showConfirmButton: false,
        });
        setSaveStatus("success");
        setTimeout(() => setSaveStatus(""), 3000);
      },
      onError: () => {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Failed to save settings",
        });
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(""), 3000);
      },
    });
  };

  const handleResetClick = () => {
    setShowResetModal(true);
  };

  const handleConfirmReset = async () => {
    setShowResetModal(false);
    setSaveStatus("saving");

    try {
      await resetSettings();
      setSaveStatus("success");
      setTimeout(() => {
        setSaveStatus("");
        window.location.reload();
      }, 2000);
    } catch (error) {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(""), 3000);
      console.error("Reset failed:", error);
    }
  };

  const handleCancelReset = () => {
    setShowResetModal(false);
  };

  const tabs = [
    { id: "general", label: "General", icon: "⚙️" },
    { id: "contact", label: "Contact", icon: "📞" },
    { id: "delivery", label: "Delivery", icon: "🚚" },
    { id: "shipping", label: "Shipping Charge", icon: "📦" },
    { id: "ServiceCharge", label: "Service Charge", icon: "💰" },
    { id: "policies", label: "Policies", icon: "📋" },
    // { id: "seo", label: "SEO", icon: "🔍" },
    { id: "maintenance", label: "Maintenance", icon: "🛠️" },
    { id: "credentials", label: "Credentials", icon: "🔐" },
  ];

  const getCurrentTabIndex = () => {
    return tabs.findIndex((tab) => tab.id === activeTab);
  };

  const handlePreviousTab = () => {
    const currentIndex = getCurrentTabIndex();
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
    }
  };

  const handleNextTab = () => {
    const currentIndex = getCurrentTabIndex();
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id);
    }
  };

  const isFirstTab = getCurrentTabIndex() === 0;
  const isLastTab = getCurrentTabIndex() === tabs.length - 1;

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return (
          <div className="tab-content">
            <div className="card-header-1">
              <h5>Website Basic Information</h5>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Website Name *
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter website name"
                  value={localSettings.websiteName || ""}
                  onChange={(e) =>
                    handleInputChange("websiteName", e.target.value)
                  }
                  required
                />
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Website Logo
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange("websiteLogo", e)}
                />
                {localSettings.websiteLogo && (
                  <div className="mt-3">
                    <SafeImage
                      src={
                        typeof localSettings.websiteLogo === "string"
                          ? localSettings.websiteLogo
                          : URL.createObjectURL(localSettings.websiteLogo)
                      }
                      alt="Website Logo Preview"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "100px",
                        objectFit: "contain",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        padding: "5px",
                      }}
                    />
                    <small className="text-muted mt-2 d-block">
                      {typeof localSettings.websiteLogo === "string"
                        ? "Current: " +
                          localSettings.websiteLogo.split("/").pop()
                        : "Selected: " + localSettings.websiteLogo.name}
                    </small>
                  </div>
                )}
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Favicon
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="file"
                  accept=".ico,image/x-icon,image/png,image/svg+xml"
                  onChange={(e) => handleFileChange("favicon", e)}
                />
                {localSettings.favicon && (
                  <div className="mt-3">
                    <SafeImage
                      src={
                        typeof localSettings.favicon === "string"
                          ? localSettings.favicon
                          : URL.createObjectURL(localSettings.favicon)
                      }
                      alt="Favicon Preview"
                      style={{
                        maxWidth: "64px",
                        maxHeight: "64px",
                        objectFit: "contain",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        padding: "5px",
                      }}
                    />
                    <small className="text-muted mt-2 d-block">
                      {typeof localSettings.favicon === "string"
                        ? "Current: " + localSettings.favicon.split("/").pop()
                        : "Selected: " + localSettings.favicon.name}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case "contact":
        return (
          <div className="tab-content">
            <div className="card-header-1">
              <h5>Contact Information</h5>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Contact Number
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="tel"
                  placeholder="Enter contact number"
                  value={localSettings.contactNumber || ""}
                  onChange={(e) =>
                    handleInputChange("contactNumber", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                WhatsApp Number
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="tel"
                  placeholder="Enter WhatsApp number"
                  value={localSettings.whatsappNumber || ""}
                  onChange={(e) =>
                    handleInputChange("whatsappNumber", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Address
              </label>
              <div className="col-md-9 col-lg-10">
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Enter business address"
                  value={localSettings.address || ""}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                />
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Email
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="email"
                  placeholder="Enter email address"
                  value={localSettings.email || ""}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Support Email
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="email"
                  placeholder="Enter support email"
                  value={localSettings.supportEmail || ""}
                  onChange={(e) =>
                    handleInputChange("supportEmail", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="card-header-1 mt-4">
              <h5>Social Media Links</h5>
            </div>
            {[
              {
                key: "facebook",
                label: "Facebook",
                icon: "fab fa-facebook-f",
                color: "#1877f2",
              },
              {
                key: "twitter",
                label: "Twitter",
                icon: "fab fa-twitter",
                color: "#1da1f2",
              },
              {
                key: "instagram",
                label: "Instagram",
                icon: "fab fa-instagram",
                color: "#e4405f",
              },
              {
                key: "linkedin",
                label: "LinkedIn",
                icon: "fab fa-linkedin-in",
                color: "#0077b5",
              },
              {
                key: "youtube",
                label: "YouTube",
                icon: "fab fa-youtube",
                color: "#ff0000",
              },
            ].map((platform) => (
              <div key={platform.key} className="mb-4 row align-items-center">
                <label className="form-label-title col-lg-2 col-md-3 mb-0">
                  <i
                    className={platform.icon}
                    style={{ color: platform.color, marginRight: "8px" }}
                  ></i>
                  {platform.label}
                </label>
                <div className="col-md-9 col-lg-10">
                  <input
                    className="form-control"
                    type="url"
                    placeholder={`https://${platform.key}.com/yourpage`}
                    value={localSettings[platform.key] || ""}
                    onChange={(e) =>
                      handleInputChange(platform.key, e.target.value)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        );

      case "delivery":
        return (
          <div className="tab-content">
            <div className="card-header-1">
              <h5>Driver Assignment</h5>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Assignment Mode
                {JSON.stringify(localSettings.driverAssignment ? "T" : "F")}
              </label>
              <div className="col-md-9 col-lg-10">
                <select
                  className="form-control"
                  value={localSettings.driverAssignment || "auto"}
                  onChange={(e) =>
                    handleInputChange("driverAssignment", e.target.value)
                  }
                >
                  <option value="auto">Auto Assignment</option>
                  <option value="manual">Manual Assignment</option>
                </select>
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Assignment Radius (km)
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="number"
                  placeholder="0"
                  value={localSettings.autoAssignmentRadius || 5}
                  onChange={(e) =>
                    handleInputChange("autoAssignmentRadius", e.target.value)
                  }
                />
              </div>
            </div>
            {/* max drivers request */}
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Max Driver Requests
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  max="100"
                  placeholder="5"
                  value={localSettings.driverAssignmentMaxRequests || 5}
                  onChange={(e) => {
                    let val = e.target.value;

                    // remove leading zeros
                    if (val !== "") {
                      val = String(parseInt(val, 10));
                    }

                    // allow only > 0
                    if (val === "" || parseInt(val, 10) > 0) {
                      handleInputChange("driverAssignmentMaxRequests", val);
                    }
                  }}
                />
                <small className="text-muted">
                  Number of drivers notified per order (👥 max 100).
                </small>
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Driver Timeout (minutes)
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="number"
                  min="5"
                  max="120"
                  placeholder="30"
                  value={localSettings.driverTimeout || 30}
                  onChange={(e) =>
                    handleInputChange("driverTimeout", e.target.value)
                  }
                />
              </div>
            </div>
              {/* commissions */}
            <div className="card-header-1 mt-4">
              <h5>product and vendor commissions</h5>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Product Commission (%)
              </label>
              <div className="col-md-9 col-lg-10">
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="0"
                    value={localSettings.minimumCommission || ""}
                    onChange={(e) => {
                      const raw = e.target.value;

                      if (raw === "") {
                        handleInputChange("minimumCommission", "");
                        return;
                      }

                      const num = parseFloat(raw);

                      if (!isNaN(num) && num >= 0 && num <= 100) {
                        handleInputChange("minimumCommission", String(num));
                      }
                    }}
                  />
              </div>
            </div>
            {/* vendor commission */}
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Vendor Commission (%)
              </label>

              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="0"
                  value={localSettings.vendorCommission || ""}
                  onChange={(e) => {
                    const raw = e.target.value;

                    if (raw === "") {
                      handleInputChange("vendorCommission", "");
                      return;
                    }

                    const num = parseFloat(raw);

                    if (!isNaN(num) && num >= 0 && num <= 100) {
                      handleInputChange("vendorCommission", String(num));
                    }
                  }}
                />
              </div>
            </div>
            <div className="card-header-1 mt-4">
              <h5>Order Place Message</h5>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Send Admin
              </label>
              <div className="col-md-9 col-lg-10">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input switch-lg"
                    type="checkbox"
                    id="orderPlaceSendAdmin"
                    checked={!!localSettings.orderPlaceSendAdmin}
                    onChange={(e) =>
                      handleInputChange("orderPlaceSendAdmin", e.target.checked)
                    }
                  />
                </div>
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Employee
              </label>
              <div className="col-md-9 col-lg-10 d-flex gap-2 align-items-center">
                <select
                  className="form-select"
                  style={{ maxWidth: "320px" }}
                  disabled={
                    !localSettings.orderPlaceSendAdmin || employeesLoading
                  }
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                >
                  <option value="">
                    {employeesLoading ? "Loading..." : "Select employee"}
                  </option>
                  {allEmployees
                    .filter(
                      (emp) =>
                        !(localSettings.orderPlaceEmployeeIds || []).includes(
                          emp.user.user_id,
                        ),
                    )
                    .map((emp) => (
                      <option key={emp.user.user_id} value={emp.user.user_id}>
                        {emp.user.name}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={
                    !localSettings.orderPlaceSendAdmin || !selectedEmployeeId
                  }
                  onClick={() => {
                    const idNum = parseInt(selectedEmployeeId, 10);
                    if (!idNum || isNaN(idNum)) return;
                    if (
                      (localSettings.orderPlaceEmployeeIds || []).includes(
                        idNum,
                      )
                    )
                      return;
                    setLocalSettings((prev) => ({
                      ...prev,
                      orderPlaceEmployeeIds: [
                        ...(prev.orderPlaceEmployeeIds || []),
                        idNum,
                      ],
                    }));
                    setSelectedEmployeeId("");
                  }}
                >
                  <i className="fas fa-plus me-1"></i>
                  Add
                </button>
              </div>
            </div>
            {(localSettings.orderPlaceEmployeeIds || []).length > 0 && (
              <div className="row">
                <div className="col-lg-2 col-md-3"></div>
                <div className="col-md-9 col-lg-10">
                  <div
                    className="table-responsive"
                    style={{ maxWidth: "520px" }}
                  >
                    <table className="table table-sm table-striped mb-0 align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: "56px" }}>#</th>
                          <th>Employee</th>
                          <th className="text-end" style={{ width: "100px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(localSettings.orderPlaceEmployeeIds || []).map(
                          (id, idx) => {
                            const numericId = parseInt(String(id), 10);
                            const emp = allEmployees.find(
                              (e) => e.user.user_id === numericId,
                            );
                            return (
                              <tr key={numericId}>
                                <td>{idx + 1}</td>
                                <td>
                                  {emp?.user?.name || `Employee #${numericId}`}
                                </td>
                                <td className="text-end">
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => {
                                      setLocalSettings((prev) => ({
                                        ...prev,
                                        orderPlaceEmployeeIds: (
                                          prev.orderPlaceEmployeeIds || []
                                        ).filter(
                                          (eid) =>
                                            parseInt(String(eid), 10) !==
                                            numericId,
                                        ),
                                      }));
                                    }}
                                  >
                                    <i className="fas fa-trash-alt me-1"></i>
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            );
                          },
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "shipping":
        return (
          <div className="tab-content">
            <div className="card-header-1 d-flex justify-content-between align-items-center">
              <div>
                <h5>Distance-Based Shipping Settings</h5>
                <p className="text-muted mb-0">
                  Configure shipping using different pricing methods
                </p>
              </div>

              {/* Toggle Button */}
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="shippingToggle"
                  checked={!!localSettings.shippingEnabled}
                  onChange={(e) => {
                    const isEnabled = e.target.checked;
                    handleInputChange("shippingEnabled", isEnabled);

                    if (!isEnabled) {
                      handleInputChange("shippingType", "");
                      handleInputChange("fixedAmount", 0);
                      handleInputChange("percentage", 0);
                      handleInputChange("baseKm", 0);
                      handleInputChange("basePrice", 0);
                      handleInputChange("pricePerKm", 0);
                    }
                  }}
                />
                <label className="form-check-label" htmlFor="shippingToggle">
                  {localSettings.shippingEnabled ? "ON" : "OFF"}
                </label>
              </div>
            </div>

            {/* Shipping Type Dropdown */}
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Shipping Type
              </label>
              <div className="col-md-9 col-lg-10">
                <select
                  className="form-control"
                  disabled={!localSettings.shippingEnabled}
                  value={localSettings.shippingType || ""}
                  onChange={(e) =>
                    handleInputChange("shippingType", e.target.value)
                  }
                >
                  <option value="">Select Shipping Type</option>
                  <option value="fixed">Fixed</option>
                  <option value="percentage">Percentage</option>
                  <option value="km_base">KM Base</option>
                </select>
              </div>
            </div>

            {/* FIXED Amount */}
            {localSettings.shippingType === "fixed" && (
              <div className="mb-4 row align-items-center">
                <label className="form-label-title col-lg-2 col-md-3 mb-0">
                  Fixed Amount (£)
                </label>
                <div className="col-md-9 col-lg-10">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    placeholder="Enter Fixed Amount"
                    value={localSettings.fixedAmount ?? ""}
                    onChange={(e) =>
                      handleInputChange("fixedAmount", e.target.value)
                    }
                  />
                </div>
              </div>
            )}

            {/* PERCENTAGE */}
            {localSettings.shippingType === "percentage" && (
              <div className="mb-4 row align-items-center">
                <label className="form-label-title col-lg-2 col-md-3 mb-0">
                  Percentage (%)
                </label>
                <div className="col-md-9 col-lg-10">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="form-control"
                    placeholder="Enter Percentage"
                    value={localSettings.percentage || ""}
                    onChange={(e) =>
                      handleInputChange("percentage", e.target.value)
                    }
                  />
                </div>
              </div>
            )}

            {/* KM BASE Fields */}
            {localSettings.shippingType === "km_base" && (
              <>
                {/* Base KM */}
                <div className="mb-4 row align-items-center">
                  <label className="form-label-title col-lg-2 col-md-3 mb-0">
                    Base Distance (KM)
                  </label>
                  <div className="col-md-9 col-lg-10">
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      placeholder="Enter Base Distance"
                      value={localSettings.baseKm || ""}
                      onChange={(e) =>
                        handleInputChange("baseKm", e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Base Price */}
                <div className="mb-4 row align-items-center">
                  <label className="form-label-title col-lg-2 col-md-3 mb-0">
                    Base Price (£)
                  </label>
                  <div className="col-md-9 col-lg-10">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      placeholder="Enter Base Price"
                      value={localSettings.basePrice || ""}
                      onChange={(e) =>
                        handleInputChange("basePrice", e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Price per KM After Base */}
                <div className="mb-4 row align-items-center">
                  <label className="form-label-title col-lg-2 col-md-3 mb-0">
                    Extra Price Per KM (£)
                  </label>
                  <div className="col-md-9 col-lg-10">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      placeholder="Enter Extra Price Per KM "
                      value={localSettings.pricePerKm || ""}
                      onChange={(e) =>
                        handleInputChange("pricePerKm", e.target.value)
                      }
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case "ServiceCharge":
        return (
          <div className="tab-content">
            {/* =================== */}
            {/* SERVICE CHARGE */}
            {/* =================== */}
            <div className="card-header-1 d-flex justify-content-between align-items-center">
              <div>
                <h5>Service Charge Settings</h5>
                <p className="text-muted mb-0">
                  Enable or disable additional service charge
                </p>
              </div>

              {/* Toggle Button */}
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="serviceChargeToggle"
                  checked={!!localSettings.serviceChargeEnabled}
                  onChange={(e) => {
                    const isEnabled = e.target.checked;
                    handleInputChange("serviceChargeEnabled", isEnabled);

                    if (!isEnabled) {
                      // Disable field and reset value
                      handleInputChange("serviceChargePercentage", 0);
                    }
                  }}
                />
                <label
                  className="form-check-label"
                  htmlFor="serviceChargeToggle"
                >
                  {localSettings.serviceChargeEnabled ? "ON" : "OFF"}
                </label>
              </div>
            </div>

            {/* Service Charge Percentage */}
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Service Charge (%)
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="form-control"
                  placeholder="Enter Service Charge Percentage"
                  value={localSettings.serviceChargePercentage || ""}
                  disabled={!localSettings.serviceChargeEnabled}
                  onChange={(e) =>
                    handleInputChange("serviceChargePercentage", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        );
      case "policies":
        return (
          <div className="tab-content">
            <div className="card-header-1">
              <h5>Policies & Terms</h5>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Terms & Conditions
              </label>
              <div className="col-md-9 col-lg-10">
                <textarea
                  className="form-control"
                  rows="8"
                  placeholder="Enter terms and conditions"
                  value={localSettings.termsConditions || ""}
                  onChange={(e) =>
                    handleInputChange("termsConditions", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Privacy Policy
              </label>
              <div className="col-md-9 col-lg-10">
                <textarea
                  className="form-control"
                  rows="8"
                  placeholder="Enter privacy policy"
                  value={localSettings.privacyPolicy || ""}
                  onChange={(e) =>
                    handleInputChange("privacyPolicy", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Refund & Return Policy
              </label>
              <div className="col-md-9 col-lg-10">
                <textarea
                  className="form-control"
                  rows="8"
                  placeholder="Enter refund and return policy"
                  value={localSettings.refundReturnPolicy || ""}
                  onChange={(e) =>
                    handleInputChange("refundReturnPolicy", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        );

      // case "seo":
      //   return (
      //     <div className="tab-content">
      //       <div className="card-header-1">
      //         <h5>SEO Settings</h5>
      //       </div>
      //       <div className="mb-4 row align-items-center">
      //         <label className="form-label-title col-lg-2 col-md-3 mb-0">
      //           Meta Title
      //         </label>
      //         <div className="col-md-9 col-lg-10">
      //           <input
      //             className="form-control"
      //             type="text"
      //             placeholder="Enter meta title"
      //             value={localSettings.metaTitle || ""}
      //             onChange={(e) =>
      //               handleInputChange("metaTitle", e.target.value)
      //             }
      //           />
      //           <small className="text-muted">
      //             Recommended: 50-60 characters
      //           </small>
      //         </div>
      //       </div>
      //       <div className="mb-4 row align-items-center">
      //         <label className="form-label-title col-lg-2 col-md-3 mb-0">
      //           Meta Description
      //         </label>
      //         <div className="col-md-9 col-lg-10">
      //           <textarea
      //             className="form-control"
      //             rows="3"
      //             placeholder="Enter meta description"
      //             value={localSettings.metaDescription || ""}
      //             onChange={(e) =>
      //               handleInputChange("metaDescription", e.target.value)
      //             }
      //           />
      //           <small className="text-muted">
      //             Recommended: 150-160 characters
      //           </small>
      //         </div>
      //       </div>
      //       <div className="mb-4 row align-items-center">
      //         <label className="form-label-title col-lg-2 col-md-3 mb-0">
      //           Meta Keywords
      //         </label>
      //         <div className="col-md-9 col-lg-10">
      //           <input
      //             className="form-control"
      //             type="text"
      //             placeholder="Enter keywords separated by commas"
      //             value={localSettings.metaKeywords || ""}
      //             onChange={(e) =>
      //               handleInputChange("metaKeywords", e.target.value)
      //             }
      //           />
      //         </div>
      //       </div>
      //       <div className="mb-4 row align-items-center">
      //         <label className="form-label-title col-lg-2 col-md-3 mb-0">
      //           Google Analytics ID
      //         </label>
      //         <div className="col-md-9 col-lg-10">
      //           <input
      //             className="form-control"
      //             type="text"
      //             placeholder="G-XXXXXXXXXX"
      //             value={localSettings.googleAnalytics || ""}
      //             onChange={(e) =>
      //               handleInputChange("googleAnalytics", e.target.value)
      //             }
      //           />
      //         </div>
      //       </div>
      //       <div className="mb-4 row align-items-center">
      //         <label className="form-label-title col-lg-2 col-md-3 mb-0">
      //           Facebook Pixel ID
      //         </label>
      //         <div className="col-md-9 col-lg-10">
      //           <input
      //             className="form-control"
      //             type="text"
      //             placeholder="XXXXXXXXXX"
      //             value={localSettings.facebookPixel || ""}
      //             onChange={(e) =>
      //               handleInputChange("facebookPixel", e.target.value)
      //             }
      //           />
      //         </div>
      //       </div>
      //     </div>
      //   );

      case "maintenance":
        return (
          <div className="tab-content">
            <div className="card-header-1">
              <h5>Maintenance Settings</h5>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Maintenance Mode
              </label>
              <div className="col-md-9 col-lg-10">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="maintenanceMode"
                    checked={localSettings.maintenanceMode || false}
                    onChange={(e) =>
                      handleInputChange("maintenanceMode", e.target.checked)
                    }
                  />
                  <label className="form-check-label" htmlFor="maintenanceMode">
                    Enable maintenance mode
                  </label>
                </div>
                {localSettings.maintenanceMode && (
                  <div className="alert alert-warning mt-2">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Website will be temporarily unavailable to users
                  </div>
                )}
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Contact Number
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="tel"
                  placeholder="Enter contact number"
                  value={localSettings.contactNumber || ""}
                  onChange={(e) =>
                    handleInputChange("contactNumber", e.target.value)
                  }
                />
                <small className="text-muted">
                  Contact number to display during maintenance
                </small>
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Maintenance Message
              </label>
              <div className="col-md-9 col-lg-10">
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder="Enter maintenance message to display to users"
                  value={localSettings.maintenanceMessage || ""}
                  onChange={(e) =>
                    handleInputChange("maintenanceMessage", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Maintenance Image
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange("maintenanceImage", e)}
                />
                {localSettings.maintenanceImage && (
                  <div className="mt-3">
                    <SafeImage
                      src={
                        typeof localSettings.maintenanceImage === "string"
                          ? localSettings.maintenanceImage
                          : URL.createObjectURL(localSettings.maintenanceImage)
                      }
                      alt="Maintenance Image Preview"
                      style={{
                        maxWidth: "300px",
                        maxHeight: "200px",
                        objectFit: "contain",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        padding: "5px",
                      }}
                    />
                    <small className="text-muted mt-2 d-block">
                      {typeof localSettings.maintenanceImage === "string"
                        ? "Current: " +
                          localSettings.maintenanceImage.split("/").pop()
                        : "Selected: " + localSettings.maintenanceImage.name}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case "credentials":
        return (
          <div className="tab-content">
            <div>
              <small className="text-muted">
                All credentials are encrypted and stored securely
              </small>
            </div>

            {/* Twilio Credentials */}
            <div className="card-header-1 mt-4">
              <h5>Twilio Credentials</h5>
              <small className="text-muted">
                For SMS notifications and phone verification
              </small>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Account SID
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter Twilio Account SID"
                  value={localSettings.twilioAccountSid || ""}
                  onChange={(e) =>
                    handleInputChange("twilioAccountSid", e.target.value)
                  }
                />
                <small className="text-muted">
                  Found in your Twilio Console dashboard
                </small>
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Auth Token
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter Twilio Auth Token"
                  value={localSettings.twilioAuthToken || ""}
                  onChange={(e) =>
                    handleInputChange("twilioAuthToken", e.target.value)
                  }
                />
                <small className="text-muted">
                  Found in your Twilio Console dashboard
                </small>
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Phone Number
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="tel"
                  placeholder="+1234567890"
                  value={localSettings.twilioPhoneNumber || ""}
                  onChange={(e) =>
                    handleInputChange("twilioPhoneNumber", e.target.value)
                  }
                />
                <small className="text-muted">
                  Your Twilio phone number (From number)
                </small>
              </div>
            </div>

            {/* Google Sign Credentials */}
            <div className="card-header-1 mt-4">
              <h5>Google Sign Credentials</h5>
              <small className="text-muted">For google sign</small>
            </div>

            {/* Google client id */}
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Google client id
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter Google client id"
                  value={localSettings.googleClientId || ""}
                  onChange={(e) =>
                    handleInputChange("googleClientId", e.target.value)
                  }
                />
                <small className="text-muted">
                  Get from Google Cloud Console
                </small>
              </div>
            </div>

            {/* Google client secret */}
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Google client secret
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter Google client secret"
                  value={localSettings.googleClientSecret || ""}
                  onChange={(e) =>
                    handleInputChange("googleClientSecret", e.target.value)
                  }
                />
                <small className="text-muted">
                  Get from Google Cloud Console
                </small>
              </div>
            </div>

            {/* Google client secret */}
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Google client secret
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter Google redirect url"
                  value={localSettings.googleRedirectUrl || ""}
                  onChange={(e) =>
                    handleInputChange("googleRedirectUrl", e.target.value)
                  }
                />
                <small className="text-muted">
                  Get from Google Cloud Console
                </small>
              </div>
            </div>

            {/* Google Maps Credentials */}
            {/* <div className="card-header-1 mt-4">
              <h5>Google Maps Credentials</h5>
              <small className="text-muted">
                For location services and maps integration
              </small>
            </div>

            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Google Maps API Key
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter Google Maps API Key"
                  value={localSettings.googleMapsApiKey || ""}
                  onChange={(e) =>
                    handleInputChange("googleMapsApiKey", e.target.value)
                  }
                />
                <small className="text-muted">
                  Get from Google Cloud Console - Maps JavaScript API
                </small>
              </div>
            </div> */}

            {/* Stripe Credentials */}
            <div className="card-header-1 mt-4">
              <h5>Stripe Payment Gateway</h5>
              <small className="text-muted">
                For payment processing and transactions
              </small>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Stripe Public Key
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter Stripe public key"
                  value={localSettings.stripePublicKey || ""}
                  onChange={(e) =>
                    handleInputChange("stripePublicKey", e.target.value)
                  }
                />
                <small className="text-muted">
                  Found in your Stripe Dashboard under API Keys
                </small>
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Stripe Secret Key
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter Stripe secret key"
                  value={localSettings.stripeSecretKey || ""}
                  onChange={(e) =>
                    handleInputChange("stripeSecretKey", e.target.value)
                  }
                />
                <small className="text-muted">
                  Found in your Stripe Dashboard under API Keys
                </small>
              </div>
            </div>

            {/* PayPal Credentials */}
            <div className="card-header-1 mt-4">
              <h5>PayPal Payment Gateway</h5>
              <small className="text-muted">
                For PayPal payment processing and transactions
              </small>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                PayPal Client ID
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter PayPal Client ID"
                  value={localSettings.paypalClientId || ""}
                  onChange={(e) =>
                    handleInputChange("paypalClientId", e.target.value)
                  }
                />
                <small className="text-muted">
                  Found in your PayPal Developer Dashboard
                </small>
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                PayPal Client Secret
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Enter PayPal Client Secret"
                  value={localSettings.paypalClientSecret || ""}
                  onChange={(e) =>
                    handleInputChange("paypalClientSecret", e.target.value)
                  }
                />
                <small className="text-muted">
                  Found in your PayPal Developer Dashboard
                </small>
              </div>
            </div>

            {/* Admin Settings */}
            <div className="card-header-1 mt-4">
              <h5>Admin Configuration</h5>
              <small className="text-muted">
                System-wide administrative settings
              </small>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="form-label-title col-lg-2 col-md-3 mb-0">
                Admin Email
              </label>
              <div className="col-md-9 col-lg-10">
                <input
                  className="form-control"
                  type="email"
                  placeholder="admin@example.com"
                  value={localSettings.adminEmail || ""}
                  onChange={(e) =>
                    handleInputChange("adminEmail", e.target.value)
                  }
                />
                <small className="text-muted">
                  The email address where administrative notifications will be
                  sent.
                </small>
              </div>
            </div>

            {/* Security Notice */}
            <div className="alert alert-info mt-4">
              <i className="fas fa-shield-alt me-2"></i>
              <strong>Security Notice:</strong> All API credentials are
              encrypted before being stored in the database. Only authorized
              administrators can view and modify these settings.
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading && Object.keys(localSettings).length === 0) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "400px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Reset to Defaults Modal */}
      {showResetModal && (
        <div className="reset-modal-overlay">
          <div className="reset-modal">
            <h4>Reset to Defaults</h4>
            <p>
              Are you sure you want to reset all settings to their default
              values? This action cannot be undone.
            </p>
            <div className="reset-modal-actions">
              <button className="btn btn-danger" onClick={handleConfirmReset}>
                Yes, Reset
              </button>
              <button className="btn btn-secondary" onClick={handleCancelReset}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="title-header option-title d-flex justify-content-between align-items-center mb-4">
        <h5>Admin Settings</h5>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleResetClick}
            disabled={loading || resetLoading}
          >
            {resetLoading ? (
              <>
                <i className="fas fa-spinner fa-spin me-1"></i>
                Resetting...
              </>
            ) : (
              <>
                <i className="fas fa-undo me-1"></i>
                Reset to Defaults
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="settings-tabs mb-4">
        <nav>
          <div className="nav nav-tabs" id="settingsTab" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
                type="button"
              >
                <span className="me-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </nav>
      </div>

      <form onSubmit={handleSubmit}>
        {renderTabContent()}

        {/* Save Status */}
        {saveStatus && (
          <div
            className={`alert ${
              saveStatus === "success"
                ? "alert-success"
                : saveStatus === "error"
                  ? "alert-danger"
                  : "alert-info"
            } mt-3`}
          >
            {saveStatus === "saving" && (
              <>
                <i className="fas fa-spinner fa-spin me-2"></i>
                {resetLoading ? "Resetting settings..." : "Saving settings..."}
              </>
            )}
            {saveStatus === "success" && (
              <>
                <i className="fas fa-check-circle me-2"></i>
                {resetLoading
                  ? "Settings reset successfully!"
                  : "Settings saved successfully!"}
              </>
            )}
            {saveStatus === "error" && (
              <>
                <i className="fas fa-exclamation-circle me-2"></i>
                {resetError
                  ? `Reset failed: ${resetError}`
                  : "Error saving settings. Please try again."}
              </>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
          <div>
            {!isFirstTab && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handlePreviousTab}
              >
                <i className="fas fa-chevron-left me-2"></i>
                Previous
              </button>
            )}
          </div>
          <div>
            {!isLastTab && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleNextTab}
              >
                Next
                <i className="fas fa-chevron-right ms-2"></i>
              </button>
            )}
          </div>
        </div>

        <div className="card-submit-button">
          <button
            className="btn btn-animation ms-auto"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin me-2"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                Save Settings
              </>
            )}
          </button>
        </div>
      </form>

      <style>{`
                .settings-tabs .nav-tabs {
                    border-bottom: 2px solid #e9ecef;
                }
                
                .settings-tabs .nav-link {
                    border: none;
                    border-bottom: 2px solid transparent;
                    color: #6c757d;
                    font-weight: 500;
                    padding: 0.75rem 1rem;
                    transition: all 0.3s ease;
                }
                
                .settings-tabs .nav-link:hover {
                    border-color: #007bff;
                    color: #007bff;
                }
                
                .settings-tabs .nav-link.active {
                    border-color: #007bff;
                    color: #007bff;
                    background: none;
                }
                
                .tab-content {
                    padding: 0rem 0;
                }
                
                .card-header-1 {
                    background: #f8f9fa;
                    padding: 1rem;
                    border-radius: 0.375rem;
                    margin-bottom: 1.5rem;
                    border-left: 4px solid #007bff;
                }
                
                .card-header-1 h5 {
                    margin: 0;
                    color: #495057;
                    font-weight: 600;
                }
                
                .form-check-input:checked {
                    background-color: #007bff;
                    border-color: #007bff;
                }
                
                /* Larger switch */
                .form-check.form-switch .switch-lg {
                    width: 3.2rem;
                    height: 1.8rem;
                }
                .form-check.form-switch .switch-lg:focus {
                    box-shadow: 0 0 0 0.25rem rgba(13,110,253,.25);
                }
                .form-check.form-switch .switch-lg:checked {
                    background-position: right center;
                }
                .form-check.form-switch .switch-lg {
                    background-size: 1.2rem 1.2rem;
                }
                
                .alert {
                    border-radius: 0.375rem;
                    border: none;
                }
                
                .btn-animation {
                    transition: all 0.3s ease;
                }
                
                .btn-animation:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                
                .card-header-1 small {
                    font-size: 0.875rem;
                    opacity: 0.8;
                }
                
                .alert-info {
                    background-color: #d1ecf1;
                    border-color: #bee5eb;
                    color: #0c5460;
                }
                
                .reset-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(0,0,0,0.4);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2000;
                }
                .reset-modal {
                    background: #fff;
                    border-radius: 12px;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.18);
                    padding: 2rem 2.5rem;
                    max-width: 400px;
                    width: 100%;
                    text-align: center;
                    animation: popIn 0.2s cubic-bezier(.4,2,.6,1) 1;
                }
                .reset-modal h4 {
                    margin-bottom: 1rem;
                    color: #d9534f;
                    font-weight: 700;
                }
                .reset-modal p {
                    color: #555;
                    margin-bottom: 2rem;
                }
                .reset-modal-actions {
                    display: flex;
                    justify-content: center;
                    gap: 1rem;
                }
                .reset-modal .btn {
                    min-width: 110px;
                    font-weight: 500;
                }
                @keyframes popIn {
                    0% { transform: scale(0.8); opacity: 0; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>
    </div>
  );
}
